import { sentryLogger } from "./sentryLogger";

export type ErrorCategory =
  | 'network'
  | 'permission'
  | 'device_not_found'
  | 'device_disconnected'
  | 'negotiation_failed'
  | 'timeout'
  | 'browser_not_supported'
  | 'livekit_cloud'
  | 'api_error'
  | 'validation_failed'
  | 'unknown';

export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface CategorizedError {
  category: ErrorCategory;
  severity: ErrorSeverity;
  isRetryable: boolean;
  userMessage: string;
  internalMessage: string;
}

/**
 * Categorizes errors for consistent handling and Sentry reporting
 */
export const categorizeError = (error: Error | unknown): CategorizedError => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorName = error instanceof Error ? error.name : 'UnknownError';

  // Permission errors
  if (errorName === 'NotAllowedError' || errorMessage.includes('permission')) {
    return {
      category: 'permission',
      severity: 'high',
      isRetryable: true,
      userMessage: 'Permission required. Please allow access and try again.',
      internalMessage: 'User denied permission or permission revoked'
    };
  }

  // Device not found errors
  if (errorName === 'NotFoundError' || errorMessage.includes('not found')) {
    return {
      category: 'device_not_found',
      severity: 'high',
      isRetryable: false,
      userMessage: 'Required device not found. Please check your hardware.',
      internalMessage: 'Camera, microphone, or display device not available'
    };
  }

  // Browser support errors
  if (errorName === 'NotSupportedError' || errorMessage.includes('not supported')) {
    return {
      category: 'browser_not_supported',
      severity: 'critical',
      isRetryable: false,
      userMessage: 'Your browser doesn\'t support this feature. Please use Chrome.',
      internalMessage: 'Browser lacks required WebRTC or media API support'
    };
  }

  // Network and connection errors
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('connection') ||
    errorMessage.includes('fetch') ||
    errorName === 'NetworkError' ||
    errorMessage.includes('ERR_NETWORK') ||
    errorMessage.includes('ERR_INTERNET_DISCONNECTED')
  ) {
    return {
      category: 'network',
      severity: 'high',
      isRetryable: true,
      userMessage: 'Network connection issue. Please check your internet.',
      internalMessage: 'Network connectivity or API endpoint unreachable'
    };
  }

  // LiveKit Cloud specific errors
  if (
    errorMessage.includes('negotiation') ||
    errorMessage.includes('UnexpectedConnectionState') ||
    errorMessage.includes('failed to publish') ||
    errorMessage.includes('LiveKit') ||
    errorMessage.includes('region') ||
    errorMessage.includes('bandwidth')
  ) {
    return {
      category: 'livekit_cloud',
      severity: 'high',
      isRetryable: true,
      userMessage: 'Connection issue. Attempting to reconnect...',
      internalMessage: 'LiveKit Cloud connectivity or negotiation issue'
    };
  }

  // Timeout errors
  if (
    errorMessage.includes('timeout') ||
    errorMessage.includes('ETIMEDOUT') ||
    errorName === 'TimeoutError'
  ) {
    return {
      category: 'timeout',
      severity: 'medium',
      isRetryable: true,
      userMessage: 'Operation timed out. Please try again.',
      internalMessage: 'Request or operation exceeded timeout threshold'
    };
  }

  // Device disconnection
  if (
    errorMessage.includes('disconnect') ||
    errorMessage.includes('track ended') ||
    errorMessage.includes('stream inactive')
  ) {
    return {
      category: 'device_disconnected',
      severity: 'medium',
      isRetryable: true,
      userMessage: 'Device disconnected. Please reconnect and try again.',
      internalMessage: 'Media device or stream was disconnected'
    };
  }

  // API errors (HTTP status codes)
  if (
    errorMessage.includes('API') ||
    errorMessage.includes('HTTP') ||
    /[45]\d\d/.test(errorMessage) // 4xx or 5xx status codes
  ) {
    return {
      category: 'api_error',
      severity: 'high',
      isRetryable: true,
      userMessage: 'Service temporarily unavailable. Please try again.',
      internalMessage: 'API endpoint returned error response'
    };
  }

  // Validation errors
  if (
    errorMessage.includes('validation') ||
    errorMessage.includes('invalid') ||
    errorMessage.includes('scope')
  ) {
    return {
      category: 'validation_failed',
      severity: 'medium',
      isRetryable: false,
      userMessage: 'Invalid input or configuration. Please check and try again.',
      internalMessage: 'Data validation or scope validation failed'
    };
  }

  // Default unknown error
  return {
    category: 'unknown',
    severity: 'medium',
    isRetryable: false,
    userMessage: 'An unexpected error occurred. Please try again.',
    internalMessage: 'Unrecognized error pattern'
  };
};

/**
 * Enhanced error logger that categorizes errors and sends to Sentry with consistent context
 */
export const logCategorizedError = (
  error: Error | unknown,
  context: {
    component: string;
    operation: string;
    callId?: string;
    organizationId?: string;
    metadata?: Record<string, any>;
  }
) => {
  const categorized = categorizeError(error);
  const errorMessage = error instanceof Error ? error.message : String(error);

  // Always log to console for local debugging
  console.error(`[${context.component}] ${context.operation} failed:`, {
    error: errorMessage,
    category: categorized.category,
    severity: categorized.severity,
    ...context.metadata
  });

  // Send to Sentry with rich context
  sentryLogger.error(`${context.operation} failed`, error, {
    component: context.component,
    operation: context.operation,
    errorCategory: categorized.category,
    errorSeverity: categorized.severity,
    isRetryable: categorized.isRetryable,
    callId: context.callId,
    organizationId: context.organizationId,
    userMessage: categorized.userMessage,
    internalMessage: categorized.internalMessage,
    ...context.metadata
  });

  return categorized;
};

/**
 * Log performance issues to Sentry
 */
export const logPerformanceIssue = (
  operation: string,
  duration: number,
  threshold: number,
  context: {
    component: string;
    callId?: string;
    organizationId?: string;
    metadata?: Record<string, any>;
  }
) => {
  const severity: ErrorSeverity = duration > threshold * 2 ? 'high' : 'medium';

  console.warn(`[${context.component}] Performance issue in ${operation}:`, {
    duration: `${duration}ms`,
    threshold: `${threshold}ms`,
    ...context.metadata
  });

  sentryLogger.warn(`Performance issue: ${operation} took ${duration}ms`, {
    component: context.component,
    operation,
    duration,
    threshold,
    severity,
    performanceIssue: true,
    callId: context.callId,
    organizationId: context.organizationId,
    ...context.metadata
  });
};

/**
 * Log network state changes with context
 */
export const logNetworkStateChange = (
  newState: 'connected' | 'disconnected' | 'reconnecting',
  reason: string,
  context: {
    component: string;
    callId?: string;
    organizationId?: string;
    metadata?: Record<string, any>;
  }
) => {
  const severity: ErrorSeverity = newState === 'disconnected' ? 'high' : 'medium';

  if (newState === 'disconnected') {
    console.warn(`[${context.component}] Network disconnected:`, { reason, ...context.metadata });
  } else {
    console.info(`[${context.component}] Network state: ${newState}:`, { reason, ...context.metadata });
  }

  sentryLogger.info(`Network state changed to ${newState}`, {
    component: context.component,
    networkState: newState,
    reason,
    severity,
    callId: context.callId,
    organizationId: context.organizationId,
    ...context.metadata
  });
};

/**
 * Log retry attempts for operations
 */
export const logRetryAttempt = (
  operation: string,
  attemptNumber: number,
  maxAttempts: number,
  error: Error | unknown,
  context: {
    component: string;
    callId?: string;
    organizationId?: string;
    metadata?: Record<string, any>;
  }
) => {
  const categorized = categorizeError(error);
  const isLastAttempt = attemptNumber >= maxAttempts;

  console.warn(`[${context.component}] Retry ${attemptNumber}/${maxAttempts} for ${operation}:`, {
    error: error instanceof Error ? error.message : String(error),
    category: categorized.category,
    isLastAttempt,
    ...context.metadata
  });

  if (isLastAttempt) {
    // Final failure - log as error
    sentryLogger.error(`${operation} failed after ${maxAttempts} attempts`, error, {
      component: context.component,
      operation,
      attemptNumber,
      maxAttempts,
      errorCategory: categorized.category,
      finalFailure: true,
      callId: context.callId,
      organizationId: context.organizationId,
      ...context.metadata
    });
  } else {
    // Retry attempt - log as warning
    sentryLogger.warn(`${operation} retry attempt ${attemptNumber}/${maxAttempts}`, {
      component: context.component,
      operation,
      attemptNumber,
      maxAttempts,
      errorCategory: categorized.category,
      retryAttempt: true,
      callId: context.callId,
      organizationId: context.organizationId,
      error: error instanceof Error ? error.message : String(error),
      ...context.metadata
    });
  }

  return categorized;
};